import {
  registerDecorator,
  validateSync,
  type ValidationOptions,
  type ValidationArguments,
} from 'class-validator';
import { plainToInstance } from 'class-transformer';
import {
  validateRgbaOrGradient,
  validateGradient,
} from '@builder4/shared/dto/styles/validators/validateColor';
import { validateRgba } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import { InteractionEventDto } from '@builder4/shared/dto/craft/props/shared';
import { LINK_VARIANT_VALUES } from '@builder4/shared/types/styles';

// WHY: Guards against malformed AI-generated Slate content — the AI can inject arbitrary
// values into text nodes that cause silent Slate editor crashes downstream.
const COLOR_REF_PATTERN = /^\$ref:#\/colors\/color[1-6]$/;

/**
 * Validates that a single text leaf (CustomText) has the correct structure.
 * Required: text (string)
 * Optional: bold, italic, underline (boolean); fontWeight, fontFamily, fontStyle,
 *           fontSize, textTransform, textDecoration, fontWeightBeforeBold (string);
 *           letterSpacing (number); color (RGBA | Gradient); textSolid (RGBA);
 *           textGradient (Gradient); selectedLinkVariant (LinkVariant);
 *           clickEvent (InteractionEvent)
 */
function validateTextLeaf(leaf: unknown): boolean {
  if (typeof leaf !== 'object' || leaf === null || Array.isArray(leaf)) {
    return false;
  }

  const record = leaf as Record<string, unknown>;

  if (!('text' in record) || typeof record.text !== 'string') {
    return false;
  }

  const booleanProps = ['bold', 'italic', 'underline'] as const;
  for (const prop of booleanProps) {
    if (prop in record && record[prop] !== undefined && typeof record[prop] !== 'boolean') {
      return false;
    }
  }

  const stringProps = [
    'fontWeight',
    'fontFamily',
    'fontStyle',
    'fontSize',
    'textTransform',
    'textDecoration',
    'fontWeightBeforeBold',
  ] as const;
  for (const prop of stringProps) {
    if (prop in record && record[prop] !== undefined && typeof record[prop] !== 'string') {
      return false;
    }
  }

  if (
    'letterSpacing' in record &&
    record.letterSpacing !== undefined &&
    typeof record.letterSpacing !== 'number'
  ) {
    return false;
  }

  if ('color' in record && record.color !== undefined && record.color !== null) {
    if (typeof record.color === 'string') {
      if (!COLOR_REF_PATTERN.test(record.color)) {
        return false;
      }
    } else if (!validateRgbaOrGradient(record.color)) {
      return false;
    }
  }

  if ('textSolid' in record && record.textSolid !== undefined && record.textSolid !== null) {
    if (typeof record.textSolid === 'string') {
      if (!COLOR_REF_PATTERN.test(record.textSolid)) {
        return false;
      }
    } else if (!validateRgba(record.textSolid)) {
      return false;
    }
  }

  if (
    'textGradient' in record &&
    record.textGradient !== undefined &&
    record.textGradient !== null
  ) {
    if (!validateGradient(record.textGradient)) {
      return false;
    }
  }

  if (
    'selectedLinkVariant' in record &&
    record.selectedLinkVariant !== undefined &&
    record.selectedLinkVariant !== null
  ) {
    if (
      typeof record.selectedLinkVariant !== 'string' ||
      !LINK_VARIANT_VALUES.includes(
        record.selectedLinkVariant as (typeof LINK_VARIANT_VALUES)[number],
      )
    ) {
      return false;
    }
  }

  if ('clickEvent' in record && record.clickEvent !== undefined && record.clickEvent !== null) {
    const dto = plainToInstance(InteractionEventDto, record.clickEvent, {
      enableImplicitConversion: true,
    });
    const errors = validateSync(dto, {
      skipMissingProperties: false,
      whitelist: false,
      forbidNonWhitelisted: false,
    });
    if (errors.length > 0) {
      return false;
    }
  }

  return true;
}

/**
 * Validates that a single Slate element (CustomElement) has the correct structure.
 * Required: type (string), children (array of valid text leaves)
 */
function validateSlateElement(element: unknown): boolean {
  if (typeof element !== 'object' || element === null || Array.isArray(element)) {
    return false;
  }

  const record = element as Record<string, unknown>;

  if (!('type' in record) || typeof record.type !== 'string') {
    return false;
  }

  if (!('children' in record) || !Array.isArray(record.children)) {
    return false;
  }

  for (const child of record.children) {
    if (!validateTextLeaf(child)) {
      return false;
    }
  }

  return true;
}

/**
 * Validates that a value is a valid Slate textStyles array.
 * Must be an array of Slate elements, each with type (string) and children (array of text leaves).
 * Each text leaf must have a text (string) property, with optional styling properties validated.
 *
 * Used to validate the textStyles prop of the Text element.
 */
export function IsTextStyles(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'isTextStyles',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown) {
          if (!Array.isArray(value)) {
            return false;
          }

          for (const element of value) {
            if (!validateSlateElement(element)) {
              return false;
            }
          }

          return true;
        },
        defaultMessage(args: ValidationArguments) {
          return `${args.property} must be a valid Slate textStyles array: each element must have type (string) and children (array of text leaves with text property)`;
        },
      },
    });
  };
}
